import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IMapField, IMapTemplate } from '../../csv-mapper/csv-map.class';
import { ChartTypes, IChartModule, IChartSchema, sortBySeverityColors } from '../charts.class';

@Component({
  selector: 'app-box-chart',
  templateUrl: './box-chart.component.html',
  styleUrls: ['./box-chart.component.sass']
})
export class BoxChartComponent implements OnInit, OnDestroy, IChartModule {

  /** Upon creating new IChartModule, copy all the codes below - START */

  /** Array of data that comes from parent component */
  @Input() set data(_data: any[]) {
    this._data = _data
    setTimeout(() => {
      this.initComp$.next()
    }, 100)
  }

  /** Mapping will need to match whatver data is required to generate the chart */
  @Input() set mapping(_mapping: IMapTemplate) {
    this._mapping = _mapping
    setTimeout(() => {
      this.initComp$.next()
    }, 100)
  }

  @Input() noDataMsg(msg: string) {
    this.ui.noDataMsg = msg
  }

  /**
   * Output event emitted when chart is clicked
   * TODO: For Charts Module
   * Set the type that's standardized across all IChartModule
   */
  @Output() chartClicked = new EventEmitter()

  /** To be read by Charts Module */
  static SCHEMA = {
    name: 'Basic Line Bar Chart',
    id: ChartTypes.boxChart,
    fields: [
      {
        text: 'Header Text',
        placeholder: 'Header text name',
        id: 'header-text',
        key: ''
      },
      {
        text: 'Header ID',
        placeholder: 'ID for header',
        id: 'header-id',
        key: ''
      },
      {
        text: 'Color Name',
        placeholder: 'Key for the color name',
        id: 'color-name',
        key: ''
      },
      {
        text: 'Color Value',
        placeholder: 'Key for the color value',
        id: 'color-value',
        key: ''
      },
      {
        text: 'Color ID',
        placeholder: 'Key for the color id',
        id: 'color-id',
        key: ''
      },
      {
        text: 'Count',
        placeholder: 'Key for the number of colors',
        id: 'color-count',
        key: ''
      }
    ]
  }

  /** ID for uniqueness (placeholder) */
  id: string
  /** Observable to trigger changes when mapping and data changes */
  initComp$: Subject<boolean> = new Subject<boolean>()
  /** Unsubber */
  unsubber: Subject<boolean> = new Subject<boolean>()

  _mapping: IMapTemplate
  _data: any[]

  @Input() _settings: {
    unsorted?: boolean
    showSum?: boolean
    hideInit?: boolean
    hideHeader?: boolean
    flipHeader?: boolean
    headerLimit?: number
    shortHeader?: boolean
  } = {}

  SCHEMA: IChartSchema = null
  /** Upon creating new IChartModule, copy all the codes above - END */

  ui: {
    data: ITempCompData[]
    noData: boolean
    noDataMsg: string,
    /** Stores the rows number (severity level) upon evaluating all header.levels */
    numRows: number[]
  } = {
      data: null,
      noData: true,
      noDataMsg: this._t.instant('No data is present'),
      numRows: []
    }

  constructor(
    private _t: TranslateService
  ) { }

  objKeys = Object.keys

  ngOnInit(): void {
    this.initComp$
      .pipe(takeUntil(this.unsubber.asObservable()))
      .subscribe((res) => {
        this.generateChart()
      })
  }

  ngOnDestroy(): void {
    this.unsubber.next()
    this.unsubber.unsubscribe()
  }

  /** 
   * This is not flexible 
   * Will need so much more refactoring
   */
  generateChart() {
    let data = this._data
      /** Temporarily set as true (remove when implementing Chart Module) */
      , mapping = this._mapping || {} as IMapTemplate

    if (!data?.length || !mapping) return

    /** Initialized object */
    let workObj: ITempCompData[] = []

    let headers: {
      [locId: string]: ITempCompData
    } = {}

    if (this.ui.noData) this.ui.noData = !this._data?.length

    const { data: mapped, fieldsObj } = this.performMapping(data, mapping)

    mapped.map((ele) => {
      const locId = ele?.headerId

      if (!locId) return

      if (!headers[ele.headerId]) {
        headers[locId] = {
          name: ele.headerName,
          id: locId,
          levels: []
        }
      }

      headers[locId].levels.push({
        id: ele.colorId,
        name: ele.colorName,
        color: ele.colorValue,
        count: ele.colorCount
      })
    })

    /** Stores the maxinum number of rows to be placed into ui.numRows */
    let numOfRows = 0

    workObj = Object.keys(headers).map((key) => {
      const header = headers[key]
        , { levels } = header

      header.levels = sortBySeverityColors(levels, 'name', true)
      
      if (numOfRows < header.levels.length)
        numOfRows = header.levels.length

      return header
    });

    this.ui.numRows = []
    for (let nIdx = 0; nIdx < numOfRows; nIdx++) {
      const lvl = numOfRows - nIdx
      this.ui.numRows.push(lvl)
    }

    workObj = workObj.map(header => {
      header.sum = header.levels.reduce((p, lvl) => {
        p += lvl.count
        return p
      }, 0)
      return header
    })

    if (!this._settings?.unsorted) {
      workObj.sort((a, b) => {
        const aSum = a.sum
          , bSum = b.sum

        return aSum < bSum ? 1 : aSum > bSum ? -1 : 0
      })
    }
    else {
      workObj.sort((a, b) => a.name < b.name ? -1 : a.name > b.name ? 1 : 0)
    }
    this.ui.data = [...workObj]
  }

  /** Emit out event when chart is clicked */
  onChartClicked(args: { name: string, header: ITempCompData, level?: ITempCompData['levels'][0] }) {
    const { name, header, level } = args

    if (!header) return

    switch (name) {
      case 'level': {
        this.chartClicked.emit(args)
        break
      }
      case 'header': {
        this.chartClicked.emit(args)
        break
      }
    }
  }

  /**
   * This is good candidate to go into IChartModule
   */
  performMapping(data: any[], mapping: IMapTemplate) {
    const { fields } = mapping

    const fieldsObj = fields.reduce((p, field) => {
      p[field.id] = field
      return p
    }, {} as { [fieldId: string]: IMapField })

    const headerId = fieldsObj['header-id']
    const headerName = fieldsObj['header-text']
    const colorName = fieldsObj['color-name']
    const colorValue = fieldsObj['color-value']
    const colorId = fieldsObj['color-id']
    const colorCount = fieldsObj['color-count']

    const mapped = data.map(ele => {
      return {
        headerId: ele[headerId.key],
        headerName: ele[headerName.key],
        colorName: ele[colorName.key],
        colorValue: ele[colorValue.key],
        colorId: ele[colorId.key],
        colorCount: ele[colorCount.key],
      }
    })

    return {
      data: mapped,
      fieldsObj
    }
  }
}

interface ITempCompData {
  /** Array index matches number of obj.levels keys */
  levels: {
    id: string
    color: string
    name: string
    count: number
  }[]
  id: string
  name: string
  sum?: number
}
